import { create } from 'zustand'

import { TeamMemberStat } from '@/api/type-response'
import { ServerUserInfo } from '@/api/type-server'
import { LoginSource } from '@/consts/team'
import {
  getPrimaryTeamTokenData,
  getTeamTokenData,
  removePrimaryTeamTokenData,
  removeTeamTokenData,
  setPrimaryTeamTokenData,
  setTeamTokenData,
  TeamTokenData,
} from '@/utils/auth'
import {
  clearLoginSource as clearLoginSourceCookie,
  getLoginSource as getLoginSourceCookie,
  setLoginSource as setLoginSourceCookie,
} from '@/utils/loginSource'

interface TeamStoreState {
  scopeInfo: ServerUserInfo['scopeInfo'] | null
  teamTokenData: TeamTokenData | null
  primaryTeamTokenData: TeamTokenData | null
  loginSource: LoginSource | null
  memberStat: TeamMemberStat | null
  teamDomainServices: Array<{
    domain: number
    services: Array<{
      label: string
      url: string
    }>
  }> | null
  teamDomainServiceMap: Record<number, string>
  setScopeInfo: (scopeInfo: ServerUserInfo['scopeInfo'] | null) => void
  setTeamContext: (tokenData: TeamTokenData, cookieExpiryDays?: number) => void
  clearTeamContext: () => void
  setPrimaryTeamContext: (tokenData: TeamTokenData, cookieExpiryDays?: number) => void
  clearPrimaryTeamContext: () => void
  updateLoginSource: (source: LoginSource) => void
  clearLoginSourceState: () => void
  initializeAuthState: () => void
  setUserRole: (role: number) => void
  setMemberStat: (stat: TeamMemberStat | null) => void
  setTeamServices: (
    services: Array<{
      domain: number
      services: Array<{
        label: string
        url: string
      }>
    }> | null,
  ) => void
}

export const useTeamStore = create<TeamStoreState>((set, get) => ({
  scopeInfo: null,
  teamTokenData: null,
  primaryTeamTokenData: null,
  loginSource: null,
  memberStat: null,
  teamDomainServices: null,
  teamDomainServiceMap: {},

  setScopeInfo(scopeInfo: ServerUserInfo['scopeInfo'] | null) {
    set(() => ({
      scopeInfo,
    }))

    // Save scopeID to cookie if in team scope (for HTTP interceptor to use)
    // Only do this if teamTokenData already exists (restoring from cookie scenario)
    // During team login, the login() method will handle setting complete teamTokenData
    if (scopeInfo?.scope === 1 && scopeInfo.scopeID) {
      const existingTeamTokenData = get().teamTokenData
      if (existingTeamTokenData && existingTeamTokenData.token) {
        // Update existing team token data with latest team info
        import('@/utils/auth').then(({ setCurrentTeamId }) => {
          setCurrentTeamId(scopeInfo.scopeID, scopeInfo.resID)
        })
      }
    }
  },

  setTeamContext(tokenData: TeamTokenData, cookieExpiryDays?: number) {
    setTeamTokenData(tokenData, cookieExpiryDays)
    set(() => ({
      teamTokenData: tokenData,
    }))

    // Start background token refresh timer
    import('@/utils/teamTokenScheduler').then(({ startTeamTokenRefreshTimer }) => {
      startTeamTokenRefreshTimer()
    })
  },

  clearTeamContext() {
    removeTeamTokenData()
    set(() => ({
      teamTokenData: null,
    }))

    // Stop background token refresh timer
    import('@/utils/teamTokenScheduler').then(({ stopTeamTokenRefreshTimer }) => {
      stopTeamTokenRefreshTimer()
    })

    // Note: Do NOT clear primaryTeamTokenData here
    // primaryTeamTokenData should only be cleared on logout
  },

  setPrimaryTeamContext(tokenData: TeamTokenData, cookieExpiryDays?: number) {
    setPrimaryTeamTokenData(tokenData, cookieExpiryDays)
    set(() => ({
      primaryTeamTokenData: tokenData,
    }))
  },

  clearPrimaryTeamContext() {
    removePrimaryTeamTokenData()
    set(() => ({
      primaryTeamTokenData: null,
    }))
  },

  updateLoginSource(source: LoginSource) {
    setLoginSourceCookie(source)
    set(() => ({
      loginSource: source,
    }))
  },

  clearLoginSourceState() {
    clearLoginSourceCookie()
    set(() => ({
      loginSource: null,
    }))
  },

  initializeAuthState() {
    const teamTokenData = getTeamTokenData()
    const primaryTeamTokenData = getPrimaryTeamTokenData()
    const loginSource = getLoginSourceCookie()

    set(() => ({
      teamTokenData,
      primaryTeamTokenData,
      loginSource,
    }))
  },

  setUserRole(role: number) {
    const currentData = get().teamTokenData
    if (currentData) {
      const updatedData = {
        ...currentData,
        userRole: role,
      }
      setTeamTokenData(updatedData)
      set({
        teamTokenData: updatedData,
      })
    }
  },

  setMemberStat(stat: TeamMemberStat | null) {
    set(() => ({
      memberStat: stat,
    }))
  },

  setTeamServices(
    services: Array<{
      domain: number
      services: Array<{
        label: string
        url: string
      }>
    }> | null,
  ) {
    const serviceMap =
      services?.reduce<Record<number, string>>((res, { domain, services }) => {
        if (services?.[0]?.url) {
          res[domain] = services[0].url
        }
        return res
      }, {}) || {}

    set(() => ({
      teamDomainServices: services,
      teamDomainServiceMap: serviceMap,
    }))
  },
}))

export const useScopeInfo = () => useTeamStore((state) => state.scopeInfo)
export const useTeamTokenData = () => useTeamStore((state) => state.teamTokenData)
export const usePrimaryTeamTokenData = () => useTeamStore((state) => state.primaryTeamTokenData)
export const useLoginSource = () => useTeamStore((state) => state.loginSource)
export const useIsTeamOnlyUser = () =>
  useTeamStore((state) => state.loginSource === LoginSource.Team)
export const useTeamUserRole = () => useTeamStore((state) => state.teamTokenData?.userRole)
export const useMemberStat = () => useTeamStore((state) => state.memberStat)
